import React, { useCallback } from 'react';
import { Chip, Switch, FormControlLabel, useTheme } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { WalteroButton, WalteroChart } from '../waltero';
import { useReportData } from '../../context/ReportDataContext';
import { sortDevicesBySerial } from '../../utils/reportSorting';

const SensorGraphs = ({ 
  showStatusColors, 
  onShowStatusColorsChange, 
  onOpenCorrectionModal 
}) => {
  const theme = useTheme();
  const { reportData } = useReportData();

  // Status-based color mapping function
  const getStatusColor = useCallback((status) => {
    console.log(`🎨 getStatusColor called with status: "${status}", showStatusColors: ${showStatusColors}`);
    if (showStatusColors && status === 'Failed read') {
      console.log(`🔴 Returning red for Failed read`);
      return theme.palette.waltero.fleetStatus.critical; // Red: #D32F2F
    }
    console.log(`🔵 Returning blue for status: "${status}"`);
    return theme.palette.waltero.primary.coreBlue; // Blue: #094872 (default for "Ok" and missing status)
  }, [theme, showStatusColors]);

  // Format status for display in tooltips
  const formatStatusForDisplay = useCallback((status) => {
    if (!status || status === 'unknown') {
      return 'Unknown';
    }
    return status;
  }, []);

  const sortedDevices = sortDevicesBySerial(reportData);

  return (
    <div className="sensors-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>All Sensor Data</h3>
        <Chip 
          label="Admin only" 
          size="small" 
          sx={{ 
            bgcolor: '#c35d2c', 
            color: 'white', 
            fontSize: '0.7rem',
            height: '20px',
            '& .MuiChip-label': {
              px: 1
            }
          }} 
        />
        
        {/* Status Color Control Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={showStatusColors}
              onChange={(e) => onShowStatusColorsChange(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label="Show status colors"
          sx={{ 
            ml: 'auto',
            '& .MuiFormControlLabel-label': {
              fontSize: '14px',
              color: theme.palette.waltero.primary.coreBlue
            }
          }}
        />
      </div>
      
      <div className="sensors-vertical">
        {sortedDevices.map((device, deviceIndex) => (
          <div key={device.deviceId} className="sensor-section">
            <div className="sensor-header">
              <h4>{device.serialNumber} - {device.meterName}</h4>
            </div>
            
            {/* Volume vs Time Line Chart - First */}
            <div className="sensor-chart-full">
              {(() => {
                if (!device || !device.dailyUsage || device.dailyUsage.length === 0) {
                  return <p>No daily volume data available for this sensor</p>;
                }
                
                // Create volume data from daily usage (using currentReading as volume)
                const volumeData = device.dailyUsage.map(day => ({
                  date: day.date,
                  volume: day.currentReading,
                  consumption: day.consumption,
                  status: day.status || 'unknown' // Keep original status, don't default to 'Ok'
                })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                
                // Debug log for status values
                console.log(`📊 SensorGraphs - Device ${device.serialNumber} status values:`, 
                  device.dailyUsage.map(day => ({ date: day.date, status: day.status })));
                
                if (volumeData.length === 0) {
                  return <p>No valid volume data available</p>;
                }
                
                const chartOption = {
                  title: {
                    text: 'Volume vs Time',
                    left: 'left',
                    top: 10
                  },
                  grid: {
                    top: 50
                  },
                  tooltip: {
                    trigger: 'axis',
                    formatter: function(params) {
                      const dataPoint = params[0];
                      const date = new Date(dataPoint.name).toLocaleDateString();
                      const volume = dataPoint.value.toFixed(2);
                      const volumeDataPoint = volumeData[dataPoint.dataIndex];
                      const consumption = volumeDataPoint.consumption.toFixed(2);
                      const status = formatStatusForDisplay(volumeDataPoint.status);
                      return `Date: ${date}<br/>Volume: ${volume}<br/>Consumption: ${consumption}<br/>Status: ${status}`;
                    }
                  },
                  xAxis: {
                    type: 'category',
                    data: volumeData.map(item => item.date)
                  },
                  yAxis: {
                    type: 'value',
                    scale: true
                  },
                  series: [{
                    type: 'line',
                    data: volumeData.map(item => item.volume),
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    itemStyle: {
                      color: (params) => getStatusColor(volumeData[params.dataIndex].status)
                    },
                    lineStyle: {
                      color: theme.palette.waltero.primary.mediumBlue // Line stays consistent color
                    }
                  }]
                };
                
                return (
                  <WalteroChart
                    option={chartOption}
                    height={300}
                  />
                );
              })()}
            </div>
            
            {/* Daily Consumption Bar Chart - Second */}
            <div className="sensor-chart-full">
              {(() => {
                if (!device || !device.dailyUsage || device.dailyUsage.length === 0) {
                  return <p>No daily consumption data available for this sensor</p>;
                }
                
                console.log(`📊 Frontend daily consumption for ${device.serialNumber}: ${device.dailyUsage.length} days`);
                
                const chartOption = {
                  title: {
                    text: 'Daily Consumption',
                    left: 'left',
                    top: 10
                  },
                  grid: {
                    top: 50
                  },
                  tooltip: {
                    trigger: 'axis',
                    formatter: function(params) {
                      const dataPoint = params[0];
                      const date = new Date(dataPoint.name).toLocaleDateString();
                      const consumption = dataPoint.value.toFixed(2);
                      const dailyUsageDataPoint = device.dailyUsage[dataPoint.dataIndex];
                      const status = formatStatusForDisplay(dailyUsageDataPoint.status);
                      return `${date}<br/>Consumption: ${consumption}<br/>Status: ${status}`;
                    }
                  },
                  xAxis: {
                    type: 'category',
                    data: device.dailyUsage.map(day => day.date)
                  },
                  yAxis: {
                    type: 'value'
                  },
                  series: [{
                    type: 'bar',
                    data: device.dailyUsage.map(day => day.consumption),
                    itemStyle: {
                      color: (params) => getStatusColor(device.dailyUsage[params.dataIndex].status)
                    }
                  }]
                };
                
                return (
                  <WalteroChart
                    option={chartOption}
                    height={300}
                  />
                );
              })()}
            </div>
            
            {/* Correct Data Button */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '10px' }}>
              <WalteroButton
                size="small"
                variant="outlined"
                onClick={() => onOpenCorrectionModal(device)}
                startIcon={<EditIcon />}
              >
                Correct Data
              </WalteroButton>
            </div>
            
            {/* Divider between sensors (except for the last one) */}
            {deviceIndex < sortedDevices.length - 1 && (
              <div className="sensor-divider">
                <hr />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SensorGraphs;